import React from "react";

import {
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Flex,
  Box,
  Button,
  useToast,
  Image,
} from "@chakra-ui/react";
import { Elements, CardNumberElement } from "@stripe/react-stripe-js";
import { FaCreditCard } from "react-icons/fa";
import { CreditCardDrawer } from "./creditCardDrawer";
import { useUpsellLogic } from "../../v2/Logic/useUpsellLogic";
import { apiAuth } from "../../Backend/api";

export const UpsellDisplay = ({
  upsell,
  booking,
  manager,
  onClose,
  isOpen,
  defaultCurrency,
  pricePreview,
}) => {
  const toast = useToast();
  
  const {
    stripePromise,
    imageUrls,
    loadingCard,
    setLoadingCard,
    isOpenPay,
    onOpenPay,
    onClosePay,
    renderedDescription,
  } = useUpsellLogic({ isOpen, booking, manager, upsell });

  const customHandleCardPayment = async ({
    stripe,
    elements,
    setLoading,
    toast,
    showUpsellPaidStep,
  }) => {
    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      toast({
        title: "Input Error",
        description: "Please fill in your card details.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    let bookingUpsellId = null;

    try {
      // Step 1: Authorize — creates a hold on the guest's card
      const authRes = await apiAuth("/v2/upsells/payment/authorize", {
        bookingId: booking.id,
        upsellId: upsell.id,
      });
      const { clientSecret } = authRes.data;
      bookingUpsellId = authRes.data.bookingUpsellId;

      // Step 2: Confirm the card with Stripe.js
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement },
        });

      if (confirmError) {
        throw new Error(confirmError.message || "Card declined. Please try again.");
      }

      // Step 3: Tell the backend the card is confirmed
      await apiAuth("/v2/upsells/payment/confirm-card", {
        bookingUpsellId,
        paymentIntentId: paymentIntent.id,
      });

      toast({
        title: "Request Sent",
        description: "We've contacted the service provider.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      showUpsellPaidStep?.();
      return true;
    } catch (error) {
      console.error("Payment error:", error);
      toast({
        title: "Payment Failed",
        description: error.message || "Something went wrong. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {stripePromise && (
        <Elements stripe={stripePromise}>
          <Drawer placement={"bottom"} onClose={onClose} isOpen={isOpen}>
            <DrawerOverlay />
            <DrawerContent
              maxH="90vh"
              borderTopRadius="2xl"
              transition="filter 0.3s ease"
              filter={isOpenPay ? "brightness(0.6)" : "brightness(1)"}
            >
              <Flex
                mx={3}
                my={4}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Text mr={4} fontSize={"18px"} fontWeight={800}>
                  {upsell.name}
                </Text>

                <DrawerCloseButton pos={"relative"} top={0} right={0} />
              </Flex>
              <DrawerBody px={0} pt={0} pb={4}>
                {imageUrls.length > 0 && (
                  <Flex mx={0} my={2} overflowX={"scroll"}>
                    {imageUrls.map((url, index) => (
                      <Box
                        key={index}
                        ml={index === 0 ? 2 : 0}
                        mr={2}
                        flexShrink={0}
                        h={"180px"}
                      >
                        <Image
                          src={url}
                          h={"170px"}
                          w={"full"}
                          maxW={"250px"}
                          objectFit={"cover"}
                          borderRadius={"lg"}
                        />
                      </Box>
                    ))}
                  </Flex>
                )}
                <Text px={3} pt={1} fontSize={"12px"} fontWeight={800} pb={1}>
                  Description
                </Text>
                {upsell.description && (
                  <Text
                    px={3}
                    dangerouslySetInnerHTML={{
                      __html: renderedDescription,
                    }}
                  />
                )}
              </DrawerBody>

              {upsell.price > 0 && (
                <DrawerFooter px={3} pb={5} flexDirection="column" gap={2}>
                  {/* Price Row */}
                  <Flex w="full" align="center" justify="space-between">
                    <Box>
                      <Text fontSize="12px" fontWeight={800} pb={1}>
                        Price
                      </Text>
                      {pricePreview ? (
                        <Flex align="baseline">
                          <Text fontSize="22px" fontWeight={800} mr={2}>
                            {(pricePreview.total / 100).toLocaleString("en-CA", {
                              style: "currency",
                              currency: pricePreview.currency.toUpperCase(),
                            })}{" "}
                            {pricePreview.currency.toUpperCase()}
                          </Text>
                        </Flex>
                      ) : (
                        <Flex align="baseline">
                          <Text fontSize="22px" fontWeight={800} mr={2}>
                            {upsell.price?.toLocaleString("en-CA", {
                              style: "currency",
                              currency: defaultCurrency,
                            })}{" "}
                            {defaultCurrency}
                          </Text>
                          {upsell.saleType === "perNight" && (
                            <Text fontSize="14px" fontWeight={800}>
                              per night
                            </Text>
                          )}
                        </Flex>
                      )}
                    </Box>
                  </Flex>

                  {/* Payment Options Row */}
                  <Flex
                    w="full"
                    justify="center"
                    align="center"
                    h="50px"
                  >
                    {/* Apple Pay disabled for now
                    <Box w="50%">
                      <ApplePayUpsell
                        id={upsell.id}
                        titleDesc={upsell.name}
                        price={pricePreview ? pricePreview.total / 100 : calculateTotalPrice()}
                        booking={booking}
                        manager={manager}
                        onClose={onClose}
                        defaultCurrency={pricePreview?.currency || defaultCurrency}
                        guestID={booking.guestID}
                        bookingID={booking.id}
                        owner={booking.owner}
                      />
                    </Box>
                    <Text
                      textAlign="center"
                      fontWeight="600"
                      fontSize="14px"
                      color="gray.500"
                    >
                      or
                    </Text>
                    */}

                    <Button
                      w="full"
                      bg="#817efc"
                      variant="solid"
                      borderRadius="10px"
                      fontSize="12px"
                      leftIcon={<FaCreditCard />}
                      isLoading={loadingCard}
                      onClick={() => {
                        if (!manager || !booking) return;
                        onOpenPay();
                      }}
                    >
                      Pay With Card
                    </Button>
                  </Flex>
                </DrawerFooter>
              )}
            </DrawerContent>
          </Drawer>

          <CreditCardDrawer
            isOpen={isOpenPay}
            onClose={onClosePay}
            handlePayment={customHandleCardPayment}
            upsellEmailFollowUp
            guestId={booking?.guestID}
            bookingId={booking?.id}
            onUpsellComplete={onClose}
          />
        </Elements>
      )}
    </>
  );
};
